import { useCallback, useEffect, useState } from 'react';
import { Card } from '@siliconx/shared/ui/Card';
import { PanelTitle } from '@siliconx/shared/ui/PanelTitle';
import { Button } from '@siliconx/shared/ui/Button';
import { Field, TextInput } from '@siliconx/shared/ui/Field';
import type { ApiKeyCreatedResponse, ApiKeySummary } from '@siliconx/shared';
import { MAX_ACTIVE_API_KEYS_PER_ORG } from '@siliconx/shared';
import { api, throwIfNotOk } from '../../lib/api';
import { useOrgFeature } from '../../lib/orgFeatures';

/**
 * SIL-2056: Settings > API keys for the data API. Org admins of business orgs
 * only. Keys belong to the ORGANIZATION: they keep working if the person who
 * created them leaves, and any org admin can revoke them. The full token is
 * shown exactly once, right after creation, and cannot be retrieved later.
 */
export function ApiKeysPanel({
  isAdmin,
  notify,
}: {
  isAdmin: boolean;
  notify: (message: string, type: 'success' | 'error') => void;
}) {
  const enabled = useOrgFeature('dataProduct');
  const [keys, setKeys] = useState<ApiKeySummary[] | null>(null);
  const [name, setName] = useState('');
  const [busy, setBusy] = useState(false);
  const [fresh, setFresh] = useState<ApiKeyCreatedResponse | null>(null);

  const load = useCallback(async () => {
    const res = await api.api.client['api-keys'].$get();
    await throwIfNotOk(res);
    const body = (await res.json()) as unknown as { keys: ApiKeySummary[] };
    setKeys(body.keys);
  }, []);

  useEffect(() => {
    if (!enabled || !isAdmin) return;
    load().catch(() => setKeys(null));
  }, [enabled, isAdmin, load]);

  if (!enabled || !isAdmin) return null;

  const active = (keys ?? []).filter((k) => !k.revokedAt && new Date(k.expiresAt) > new Date());
  const atLimit = active.length >= MAX_ACTIVE_API_KEYS_PER_ORG;

  async function create() {
    setBusy(true);
    try {
      const res = await api.api.client['api-keys'].$post({ json: { name: name.trim() } });
      if (res.status === 409) {
        notify('Maximum active keys reached. Revoke one first.', 'error');
        return;
      }
      if (res.status === 403) {
        notify('An active data subscription is required to create API keys.', 'error');
        return;
      }
      await throwIfNotOk(res);
      setFresh((await res.json()) as unknown as ApiKeyCreatedResponse);
      setName('');
      await load();
    } catch {
      notify('Could not create the API key.', 'error');
    } finally {
      setBusy(false);
    }
  }

  async function revoke(id: string) {
    try {
      const res = await api.api.client['api-keys'][':id'].$delete({ param: { id } });
      await throwIfNotOk(res);
      notify('API key revoked.', 'success');
      await load();
    } catch {
      notify('Could not revoke that API key.', 'error');
    }
  }

  const fmt = (iso: string) => new Date(iso).toLocaleDateString();
  return (
    <Card className="space-y-4">
      <PanelTitle
        title="API keys"
        description="Keys authenticate your organization to the SiliconX data API. They belong to the organization, not to you: they keep working if you leave, and any organization admin can revoke them."
      />

      {fresh && (
        <div className="space-y-2 rounded-md border border-amber-300 bg-amber-50 p-3 dark:border-amber-800 dark:bg-amber-950/40">
          <div className="text-sm font-medium text-foreground">
            Copy your new key now. It will not be shown again.
          </div>
          <code className="block break-all rounded bg-card p-2 font-num text-xs" data-testid="new-api-key">
            {fresh.token}
          </code>
          <div className="flex gap-2">
            <Button
              size="sm"
              variant="secondary"
              onClick={() => {
                void navigator.clipboard?.writeText(fresh.token);
                notify('Copied.', 'success');
              }}
            >
              Copy
            </Button>
            <Button size="sm" variant="secondary" onClick={() => setFresh(null)}>
              I have saved it
            </Button>
          </div>
        </div>
      )}

      <div className="flex items-end gap-2">
        <Field label="New key name" className="flex-1">
          <TextInput
            value={name}
            maxLength={100}
            placeholder="e.g. Pricing pipeline"
            onChange={(e) => setName(e.target.value)}
          />
        </Field>
        <Button disabled={busy || atLimit || name.trim().length === 0} onClick={() => void create()}>
          Create key
        </Button>
      </div>
      {atLimit && (
        <div className="text-xs text-muted-foreground">
          Maximum of {MAX_ACTIVE_API_KEYS_PER_ORG} active keys reached. Revoke one to create another.
        </div>
      )}

      {keys && keys.length > 0 && (
        <table className="w-full text-left text-sm">
          <thead className="text-xs text-muted-foreground">
            <tr>
              <th className="py-1 font-medium">Name</th>
              <th className="py-1 font-medium">Key</th>
              <th className="py-1 font-medium">Last used</th>
              <th className="py-1 font-medium">Expires</th>
              <th className="py-1" />
            </tr>
          </thead>
          <tbody>
            {keys.map((k) => {
              const expired = new Date(k.expiresAt) <= new Date();
              return (
                <tr key={k.id} className="border-t border-border">
                  <td className="py-1.5">{k.name}</td>
                  <td className="py-1.5 font-num text-xs">{k.prefix}...</td>
                  <td className="py-1.5">{k.lastUsedAt ? fmt(k.lastUsedAt) : 'Never'}</td>
                  <td className="py-1.5">
                    {k.revokedAt ? 'Revoked' : expired ? 'Expired' : fmt(k.expiresAt)}
                  </td>
                  <td className="py-1.5 text-right">
                    {!k.revokedAt && !expired && (
                      <Button size="sm" variant="destructive" onClick={() => void revoke(k.id)}>
                        Revoke
                      </Button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
    </Card>
  );
}
